import React, { useContext } from "react";
import { Button, Form, Modal } from "carbon-components-react";
import { Formik } from "formik";
import { TrashCan16 } from "@carbon/icons-react";

import { ActualEscrow } from "@eave/shared/types/api";

import DollarInput from "web_modules/shared/carbon/DollarInput";

import { AmortizationContext } from "../AmortizationProvider";
import { dateToShortForm } from "../helpers";

import { getInitialValues, validationSchema } from "./validation";

const styles = require("./styles.module.scss");

interface Props {
  period?: string;
  actualEscrowId?: number;
  onClose(): void;
}

const AddOrEditActualEscrowModal = ({ period, actualEscrowId, onClose }: Props) => {
  const { busy, actualEscrows, updateActualEscrow, createActualEscrow, deleteActualEscrow } =
    useContext(AmortizationContext);

  const actualEscrow = actualEscrows.find(p => p.id === actualEscrowId);

  const headingAction = actualEscrow ? "Editing" : "Adding";
  const headingDate = dateToShortForm(actualEscrow ? actualEscrow.payment_period : period);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={getInitialValues(actualEscrow, period)}
      onSubmit={values => {
        if (actualEscrow) {
          updateActualEscrow({
            ...actualEscrow,
            ...values,
          } as ActualEscrow).then(onClose);
          return;
        }
        createActualEscrow({
          payment_period: period,
          ...values,
        } as ActualEscrow).then(onClose);
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, values }) => (
        <Modal
          open
          size="sm"
          modalHeading={`${headingAction} actual escrows for period starting ${headingDate}`}
          primaryButtonText={`${actualEscrow ? "Update" : "Save"} actual escrows`}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={busy || !!Object.keys(errors).length}
          onRequestSubmit={handleSubmit}
          onRequestClose={onClose}
        >
          <Form>
            <div className={styles.field}>
              <Button
                kind="danger"
                renderIcon={TrashCan16}
                disabled={!actualEscrow}
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this escrow?")) {
                    onClose();
                    deleteActualEscrow(actualEscrow);
                  }
                }}
              >
                Delete
              </Button>
            </div>
            <div className={styles.field}>
              <DollarInput
                id="actual-tax-payment"
                name="actual_tax_payment"
                value={values.actual_tax_payment}
                labelText="Actual escrow tax payment"
                invalid={!!errors.actual_tax_payment}
                invalidText={errors.actual_tax_payment}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="actual-insurance-payment"
                name="actual_insurance_payment"
                value={values.actual_insurance_payment}
                labelText="Actual escrow insurance payment"
                invalid={!!errors.actual_insurance_payment}
                invalidText={errors.actual_insurance_payment}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="actual-mortgage-insurance-payment"
                name="actual_mortgage_insurance_payment"
                value={values.actual_mortgage_insurance_payment}
                labelText="Actual escrow MI payment"
                invalid={!!errors.actual_mortgage_insurance_payment}
                invalidText={errors.actual_mortgage_insurance_payment}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="actual-disbursement-amount"
                name="actual_disbursement_amount"
                value={values.actual_disbursement_amount}
                labelText="Actual disbursement amount"
                invalid={!!errors.actual_disbursement_amount}
                invalidText={errors.actual_disbursement_amount}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default AddOrEditActualEscrowModal;
